import { cn } from "@qw/ui";
import type { ComponentPropsWithoutRef, FC, ReactNode } from "react";
import ReactMarkdown from "react-markdown";

interface StreamedMarkdownProps {
  /**
   * The already-revealed prefix to render right now. This component is
   * render-only — it does NOT own the reveal. Each surface keeps its own reveal
   * orchestration (qw-chat's message-level sequential cursor; alpine's
   * single-part `useSmoothStreamedText`) and passes the current prefix here.
   */
  displayText: string;
  /**
   * The raw, full text of the part. `revealComplete` is derived as
   * `displayText === fullText`, which gates {@link afterComplete}. Pass the same
   * value as `displayText` when there is no separate reveal (e.g. reduced-motion
   * or historical turns) so extras render immediately.
   */
  fullText: string;
  components?: ComponentPropsWithoutRef<typeof ReactMarkdown>["components"];
  remarkPlugins?: ComponentPropsWithoutRef<typeof ReactMarkdown>["remarkPlugins"];
  /** Appended to the shared `prose prose-sm max-w-none` shell (e.g. `dark:prose-invert`). */
  className?: string;
  /**
   * Rendered after the markdown, but only once the reveal is complete
   * (`displayText === fullText`). qw-chat passes its suggestion-insert buttons
   * here so they never appear under a still-revealing answer; alpine passes none.
   */
  afterComplete?: ReactNode;
}

/**
 * The shared streamed-text leaf for Alby's two chat surfaces (qw-chat's
 * `AssistantTextChatSessionMessagePart` and alpine-coach's `TextMessagePart`).
 *
 * It owns the pieces those surfaces kept duplicating — the `prose` shell, the
 * `revealComplete = displayText === fullText` derivation, and the
 * gate-extras-until-complete rule — while staying context-free and props-only so
 * the two providers (`ChatProvider` vs standalone `@ai-sdk/react`) need not
 * merge. The differing bits (markdown `components`, `remarkPlugins`, `className`
 * extras, and the {@link afterComplete} slot) are injected. See B-1457.
 */
export const StreamedMarkdown: FC<StreamedMarkdownProps> = ({
  displayText,
  fullText,
  components,
  remarkPlugins,
  className,
  afterComplete,
}) => {
  const revealComplete = displayText === fullText;

  return (
    <div className={cn("prose prose-sm max-w-none", className)}>
      <ReactMarkdown components={components} remarkPlugins={remarkPlugins}>
        {displayText}
      </ReactMarkdown>
      {revealComplete && afterComplete}
    </div>
  );
};
